import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import NotificationsSvg from "@assets/svgs/Notifications";
import NotificationList from "./NotificationList";
import { useOnOutsideClick } from "@hooks/useOnOutsideClick";
import { useNotifications } from "./useNotifications";
import "./Notifications.css";

const NotificationIcon = ({
  icon: Icon = NotificationsSvg,
  typeFilter = "alerts",
  ariaLabel = "Notifications",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    markRead,
    markAllRead,
    refetch,
  } = useNotifications({ typeFilter });
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next) refetch({ silent: true });
      return next;
    });
  }, [refetch]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  useOnOutsideClick(wrapperRef, handleClose);

  const handleNotificationClick = useCallback(
    async (notification) => {
      if (!notification) return;
      await markRead(notification._id);
      setIsOpen(false);
      if (notification.action_url) {
        navigate(notification.action_url);
      }
    },
    [markRead, navigate],
  );

  return (
    <div
      className="notification-wrapper"
      ref={wrapperRef}
      onKeyDown={(event) => {
        if (event.key === "Escape" && isOpen) {
          event.preventDefault();
          handleClose();
          triggerRef.current?.focus();
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`notification-btn ${isOpen ? "active" : ""}`}
        onClick={handleToggle}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <Icon
          size={20}
          width="20"
          height="20"
          aria-hidden="true"
          focusable="false"
        />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="notification-dropdown"
          role="dialog"
          aria-label={ariaLabel}
        >
          <NotificationList
            notifications={notifications}
            isLoading={isLoading}
            error={error}
            onNotificationClick={handleNotificationClick}
            onMarkAllRead={markAllRead}
          />
        </div>
      )}
    </div>
  );
};

export default NotificationIcon;
